import { Box, Flex, Stack, Table, Tbody, Td, Text, Tr } from '@chakra-ui/react'
import type { DetailedUser } from 'BrokerTsLib/src/dtos'
import { getUserTypeName } from 'brokertslib/src/enums'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { useMemo } from 'react'

import { Card, CardBody } from '../../components/Card'
import { useAuth } from '../../context/AuthContext'
import { External, Group, Key, User as UserIcon, UserInfo } from '../../icons'
import { AcquisitionNumbersTable } from '../shared/AcquisitionNumbersTable'
import { OrganizationLink } from '../shared/OrganizationLink'
import { SectionHeading } from '../shared/SectionHeading'
import { UserLink } from '../shared/UserLink'
import {
  UserDetailItemLabel,
  UserDetailItemValue,
} from './containers/UserDetailItem'
import { UserHeader } from './containers/UserHeader'
import { UserFlexGroups } from './UserFlexGroups'

export const UserDetail = ({ user }: { user: DetailedUser }) => {
  const {
    userId,
    organizationUnitId,
    organizationUnitName,
    userTypeId,
    roles,
    userName,
    userNameOtherSystem,
    personalNumbers,
    personalNumbersProperty,
    dateModified,
    modifiedBy,
    modifiedById,
  } = user

  const auth = useAuth()

  const isCurrentUser = useMemo(
    () => auth?.user?.id === user.userId,
    [auth, user.userId],
  )

  const personalNumbersFiltered = useMemo(
    () =>
      [
        ...personalNumbers,
        ...personalNumbersProperty.map((pnp) => ({
          ...pnp,
          insurerName: pnp.insurerName + ' (pojištění majetku)',
        })),
      ].filter((pn) => pn.acquisitionNumber),
    [personalNumbers, personalNumbersProperty],
  )

  return (
    <Stack gap={6}>
      <UserHeader {...user} isCurrentUser={isCurrentUser} />

      <Card>
        <CardBody sx={{ p: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { base: '1fr', lg: '3fr 4fr 1fr' },
              alignItems: 'start',
              columnGap: 8,
              rowGap: 4,
            }}
          >
            <Flex
              h={'100%'}
              flexDir={'column'}
              justifyContent={'space-between'}
              gap={4}
            >
              <Table variant="unstyled">
                <Tbody>
                  <UserDetailItem
                    label={
                      <>
                        <Group /> Organizace
                      </>
                    }
                  >
                    <OrganizationLink
                      id={organizationUnitId}
                      name={organizationUnitName}
                      showId={true}
                    />
                  </UserDetailItem>
                  <UserDetailItem
                    label={
                      <>
                        <UserInfo /> Typ uživatele
                      </>
                    }
                  >
                    {getUserTypeName(userTypeId)}
                  </UserDetailItem>
                  <UserDetailItem
                    label={
                      <>
                        <Key /> Role
                      </>
                    }
                  >
                    {roles.map((x) => x.roleName).join(', ')}
                  </UserDetailItem>
                  <UserDetailItem
                    label={
                      <>
                        <UserIcon /> Uživatelské jméno
                      </>
                    }
                  >
                    {userName}
                  </UserDetailItem>
                  {userNameOtherSystem && (
                    <UserDetailItem
                      label={
                        <>
                          <External /> Uživatelské jméno
                          <br />
                          do&nbsp;jiných systémů
                        </>
                      }
                    >
                      {userNameOtherSystem}
                    </UserDetailItem>
                  )}
                </Tbody>
              </Table>
              {dateModified && (
                <Text
                  sx={{
                    color: 'gray.500',
                    fontSize: 'sm',
                    alignContent: 'end',
                  }}
                >
                  Poslední změna: {formatDateTime(dateModified)}{' '}
                  <UserLink name={modifiedBy} userId={modifiedById} />
                </Text>
              )}
            </Flex>

            <Box sx={{ display: 'grid' }}>
              <SectionHeading sx={{ pl: 4 }}>Osobní čísla</SectionHeading>
              {personalNumbersFiltered && personalNumbersFiltered.length > 0 ? (
                <AcquisitionNumbersTable rows={personalNumbersFiltered} />
              ) : (
                <Text pl={4}>Žádná osobní čísla</Text>
              )}
            </Box>
          </Box>
        </CardBody>
      </Card>

      {auth?.user?.isSupervisor &&
        <UserFlexGroups userId={userId} />
      }
    </Stack>
  )
}

const UserDetailItem = ({
  label,
  children,
}: {
  label: React.ReactNode
  children: React.ReactNode
}) => {
  return (
    <Tr>
      <Td py={0.5}>
        <UserDetailItemLabel>{label}</UserDetailItemLabel>
      </Td>
      <Td py={0.5}>
        <UserDetailItemValue>{children}</UserDetailItemValue>
      </Td>
    </Tr>
  )
}
